/**
 * "Add items to the RMA" card.
 * Renders a single headline and a single items table for the whole session; a mixed order adds one
 * collapsible group header row per member order above that member's item rows.
 */
import {observer} from 'mobx-react-lite';
import React from 'react';
import {FontBody2} from "../../../components/fonts/font-body2/font-body2";
import FontHeading4 from "../../../components/fonts/font-heading4/font-heading4";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import Card from "../../../components/layouts/card/card";
import Table from "../../../components/table/table";
import {Tooltip} from "../../../components/tooltip/tooltip";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import {rootContainer, TYPE} from "../../../services/container/root";
import IsMobileService from "../../../services/is-mobile-service/is-mobile-service";
import useCollapsedGroups from '../hooks/use-collapsed-groups';
import type { CreateRmaMainStore } from '../stores/create-rma-main-store';
import FulfillmentSectionHeader from './fulfillment-section-header';
import './order-product-list-card.scss';
import OrderProductListSection from './order-product-list-section';
import {getOrderProductTableColumns, ORDER_PRODUCT_LIST_SCOPE} from './order-product-table-columns';

interface IProps {
    createRmaMainStore: CreateRmaMainStore;
}

const OrderProductListCard: React.FC<IProps> = observer(({ createRmaMainStore }) => {
	const { getTranslation } = useDashboardTranslation();
	const { isExpanded, toggle } = useCollapsedGroups();
	const isIPad = rootContainer.get<IsMobileService>(TYPE.IsMobileService).isIPad();
	const tableColumns = getOrderProductTableColumns(getTranslation, isIPad);
	const { isMixedOrder, drafts, settings: createRmaSettingsStore } = createRmaMainStore;

	const totalProducts = drafts.reduce((total, draft) => total + draft.orderStore.totalProducts, 0);
	const returnedProducts = drafts.reduce((total, draft) => total + draft.orderStore.returnedProducts, 0);
	const removedProducts = drafts.reduce((total, draft) => total + draft.orderStore.removedProducts, 0);

	return (
		<div className='order-product-list-card'>
			<Card>
				<div className={'headline'}>
					<Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('ONLY_FULFILLED_ITEMS', ORDER_PRODUCT_LIST_SCOPE)}</FontBody2>}><FontHeading4 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.NEUTRAL10}>{getTranslation('ADD_ITEMS_TO_RMA', ORDER_PRODUCT_LIST_SCOPE)}</FontHeading4></Tooltip> <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
						{totalProducts} {getTranslation('PURCHASED_ITEMS', ORDER_PRODUCT_LIST_SCOPE)}
						{returnedProducts ? ' / ' + returnedProducts + ' ' + getTranslation('RETURNED_ITEMS', ORDER_PRODUCT_LIST_SCOPE): ''}
						{removedProducts ? ' / ' + removedProducts + ' ' + getTranslation('REMOVED_ITEMS', ORDER_PRODUCT_LIST_SCOPE): ''}
					</FontBody2>
				</div>
				<Table columns={tableColumns}>
					{drafts.map((draft) => {
						const isGroupExpanded = isExpanded(draft.orderStore.orderId);
						return (
							<React.Fragment key={draft.orderStore.orderId}>
								<FulfillmentSectionHeader
									draft={draft}
									isMixedOrder={isMixedOrder}
									columnsCount={tableColumns.length}
									isExpanded={isGroupExpanded}
									onToggle={() => toggle(draft.orderStore.orderId)}
								/>
								{isGroupExpanded &&
									<OrderProductListSection
										rmaRequestStore={draft.requestStore}
										orderStore={draft.orderStore}
										createRmaSettingsStore={createRmaSettingsStore}
										isIPad={isIPad}
										isRMACreated={draft.isRMACreated}
									/>
								}
							</React.Fragment>
						);
					})}
				</Table>
			</Card>
		</div>
	)
});

export default OrderProductListCard;
